import type { Editor, TLAsset, TLBinding, TLShapePartial } from '@tldraw/tldraw'
import type { TldrawSnapshot } from '@/service/api'

// Cards and comment pins are rebuilt by the board from the document list and
// saved comments, so we skip them here. Restoring them would duplicate them and
// fight the grid layout.
const MANAGED_SHAPE_TYPES = new Set(['DocPreview', 'ConnectorCard', 'CommentPin'])

interface SnapshotRecord {
  id?: string
  typeName?: string
  type?: string
  parentId?: string
  fromId?: string
  toId?: string
  [key: string]: unknown
}

interface RestorableContent {
  assets: TLAsset[]
  shapes: TLShapePartial[]
  bindings: TLBinding[]
}

const getStore = (
  snapshot: TldrawSnapshot | null | undefined,
): Record<string, SnapshotRecord> | null => {
  const store = (snapshot?.document as { store?: Record<string, SnapshotRecord> } | undefined)?.store
  return store && typeof store === 'object' ? store : null
}

// The user's own shapes — drawings, text, arrows, images, etc. — i.e. anything
// that is not a board-managed card or pin. Page-level shapes are reparented to
// the live page (saved page ids don't survive a fresh editor); shapes inside a
// group or frame keep their parent. Does not mutate the snapshot.
export const selectRestorableShapes = (
  snapshot: TldrawSnapshot | null | undefined,
  currentPageId: string,
): TLShapePartial[] => {
  const store = getStore(snapshot)
  if (!store) return []

  const shapes: TLShapePartial[] = []
  for (const record of Object.values(store)) {
    if (record?.typeName !== 'shape' || !record.type) continue
    if (MANAGED_SHAPE_TYPES.has(record.type)) continue

    const parentId =
      typeof record.parentId === 'string' && record.parentId.startsWith('page:')
        ? currentPageId
        : record.parentId
    shapes.push({ ...record, parentId } as TLShapePartial)
  }
  return shapes
}

// Split the snapshot into what's worth restoring: the user's shapes, the image
// assets they use, and the arrow bindings between them. A binding is kept only
// when both ends are restored shapes — one pointing at a rebuilt card would
// dangle, so that arrow comes back unbound instead.
//
// Page, camera, instance and session records are dropped on purpose: restoring
// them would move the camera and fight the grid layout.
export const partitionRestorableContent = (
  snapshot: TldrawSnapshot | null | undefined,
  currentPageId: string,
): RestorableContent => {
  const store = getStore(snapshot)
  if (!store) return { assets: [], shapes: [], bindings: [] }

  const shapes = selectRestorableShapes(snapshot, currentPageId)
  const shapeIds = new Set<string>(shapes.map((s) => String(s.id)))
  // Keep only assets a restored shape references (via props.assetId); skips
  // orphaned assets left in the snapshot.
  const referencedAssetIds = new Set<string>(
    shapes
      .map((s) => (s.props as { assetId?: string } | undefined)?.assetId)
      .filter((id): id is string => typeof id === 'string'),
  )

  const assets: TLAsset[] = []
  const bindings: TLBinding[] = []
  for (const record of Object.values(store)) {
    if (record?.typeName === 'asset') {
      if (typeof record.id === 'string' && referencedAssetIds.has(record.id)) {
        assets.push(record as unknown as TLAsset)
      }
    } else if (
      record?.typeName === 'binding' &&
      typeof record.fromId === 'string' &&
      typeof record.toId === 'string' &&
      shapeIds.has(record.fromId) &&
      shapeIds.has(record.toId)
    ) {
      bindings.push(record as unknown as TLBinding)
    }
  }
  return { assets, shapes, bindings }
}

// Sort key for the per-shape fallback: shapes on the page come before shapes
// nested in a group or frame, so a parent is created before its child.
const pageParentRank = (shape: TLShapePartial): number =>
  typeof shape.parentId === 'string' && shape.parentId.startsWith('page:') ? 0 : 1

// Create all shapes in one batch; if tldraw rejects it because of one bad
// record, retry shape by shape (parents first) so a single corrupt shape can't
// drop the rest.
const createShapesResilient = (editor: Editor, shapes: TLShapePartial[]): void => {
  if (shapes.length === 0) return
  try {
    editor.createShapes(shapes)
  } catch (error) {
    console.error('[BoardCanvas] Batch shape restore failed, retrying per-shape:', error)
    const ordered = [...shapes].sort((a, b) => pageParentRank(a) - pageParentRank(b))
    for (const shape of ordered) {
      try {
        editor.createShapes([shape])
      } catch (shapeError) {
        console.error(`[BoardCanvas] Skipped unrestorable shape ${shape.id}:`, shapeError)
      }
    }
  }
}

// Restore the user's hand-added content from a saved board snapshot: drawings,
// text, shapes, arrows (with their bindings) and pasted images. Runs as a remote
// change so it doesn't look like a fresh user edit and re-trigger autosave.
// Best-effort — any failure is logged and the board still opens.
export const restoreUserContent = (
  editor: Editor,
  snapshot: TldrawSnapshot | null | undefined,
): void => {
  try {
    const { assets, shapes, bindings } = partitionRestorableContent(snapshot, editor.getCurrentPageId())
    if (assets.length === 0 && shapes.length === 0) return

    editor.store.mergeRemoteChanges(() => {
      // Order matters: shapes can reference assets, bindings reference shapes.
      const newAssets = assets.filter((a) => !editor.getAsset(a.id))
      if (newAssets.length > 0) {
        try {
          editor.createAssets(newAssets)
        } catch (error) {
          console.error('[BoardCanvas] Asset restore failed:', error)
        }
      }

      createShapesResilient(editor, shapes)

      if (bindings.length > 0) {
        try {
          editor.createBindings(bindings)
        } catch (error) {
          console.error('[BoardCanvas] Binding restore failed:', error)
        }
      }
    })
  } catch (error) {
    console.error('[BoardCanvas] Failed to restore content from snapshot:', error)
  }
}
